import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'
import ScanForm from '../components/ScanForm'
import SecurityAnimation from '../components/SecurityAnimation'
import { useAuthStore } from '../lib/auth'
import { scanTarget } from '../lib/api'

export default function FreeScan() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()
  const landingState = location.state || {}

  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    document.title = 'Free Job Scam Checker — Verify Any Recruiter Instantly | ScamView'
  }, [])

  // If user is logged in, redirect to real dashboard
  useEffect(() => {
    if (user) {
      navigate('/dashboard', { state: landingState, replace: true })
    }
  }, [user, navigate, landingState])

  const handleFreeScan = async (targetType, targetValue, userContext) => {
    setLoading(true)
    setResult(null)
    setError(null)

    try {
      const data = await scanTarget(targetType, targetValue, userContext)
      setResult(data)
    } catch (err) {
      if (err.rateLimited) {
        setError('rateLimited')
      } else {
        setError(err.message || 'Scan failed. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const signalCount = result?.internal_analysis?.length || 0

  return (
    <div className="min-h-screen bg-[#0a0e17]">
      <Header />

      <main className="container mx-auto px-4 py-12 max-w-5xl">
        {!result && !loading && (
          <>
            <div className="mb-6">
              <button
                onClick={() => navigate('/')}
                className="text-gray-400 hover:text-white transition text-sm mb-4 inline-block"
              >
                &larr; Back to Home
              </button>
              <h2
                className="text-2xl font-bold text-white"
                style={{ fontFamily: "'Orbitron', sans-serif" }}
              >
                Free Scan Preview
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                Enter an email or phone number to scan. Sign up to see full results.
              </p>
            </div>

            {error === 'rateLimited' ? (
              <div className="card text-center py-10">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-500/20 border-2 border-yellow-500/40 flex items-center justify-center">
                  <svg className="w-8 h-8 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H10m12-6a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Free Scan Limit Reached</h3>
                <p className="text-gray-400 mb-6 max-w-md mx-auto">
                  You've used your 3 free scans for today. Create an account to unlock
                  full scan results, history tracking, and more.
                </p>
                <button
                  onClick={() => navigate('/plans')}
                  className="btn-primary text-lg mb-3"
                >
                  View Plans
                </button>
                <p className="text-gray-500 text-sm">
                  Already have an account?{' '}
                  <Link to="/login" className="text-cyan-400 hover:text-cyan-300 transition font-semibold">
                    Login
                  </Link>
                </p>
              </div>
            ) : (
              <>
                {error && (
                  <div className="bg-red-500/20 text-red-400 p-4 rounded-lg mb-6 text-center border border-red-500/30">
                    {error}
                  </div>
                )}

                <ScanForm
                  onScan={handleFreeScan}
                  loading={loading}
                  initialType={landingState.type}
                  initialValue={landingState.value}
                />
              </>
            )}
          </>
        )}

        {/* Loading state */}
        {loading && (
          <div className="card text-center py-16">
            <div className="inline-block w-12 h-12 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin mb-4" />
            <p className="text-gray-300 text-lg">Analyzing target...</p>
            <p className="text-gray-500 text-sm mt-1">Running AI analysis, DNS checks, and community reports</p>
          </div>
        )}

        {/* Results — two-column layout */}
        {result && !loading && (
          <>
            <button
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-white transition text-sm mb-6 inline-block"
            >
              &larr; Back to Home
            </button>

            <div className="grid md:grid-cols-[280px_1fr] gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Rating Found Card */}
                <div className="card text-center py-8">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-500/20 border-2 border-red-500/40 flex items-center justify-center">
                    <svg className="w-10 h-10 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01M12 3l9.09 16.91H2.91L12 3z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-white tracking-wider uppercase">Rating Found</h3>
                </div>

                {/* Identity Protected — Animated */}
                <SecurityAnimation />
                <p className="text-gray-400 text-xs text-center leading-relaxed">
                  Sign up to see your full results, track scan history, and protect your inbox with HireMail.
                </p>
                <div className="flex items-center justify-center gap-2 text-gray-500 text-xs">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  Data encrypted in transit and at rest
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Blurred Summary */}
                <div className="card relative overflow-hidden">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`w-3 h-3 rounded-full ${
                      result.risk_score >= 7 ? 'bg-red-500' :
                      result.risk_score >= 4 ? 'bg-yellow-500' : 'bg-green-500'
                    }`} />
                    <h3 className="text-lg font-bold text-white">Summary</h3>
                  </div>
                  <div className="relative">
                    <p className="text-gray-300 leading-relaxed select-none" style={{
                      filter: 'blur(5px)',
                      WebkitFilter: 'blur(5px)',
                      userSelect: 'none'
                    }}>
                      {result.ai_summary || 'The AI has detected multiple high risk signals in this communication. The identity behind this contact has raised additional suspicions.'}
                    </p>
                  </div>
                  <p className="text-gray-500 text-xs mt-3 italic">
                    Analysis hidden. Please log in to view the specific findings breakdown.
                  </p>
                </div>

                {/* Active Intelligence Located */}
                <div className="card text-center py-8">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <svg className="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <h3 className="text-sm font-bold text-yellow-400 uppercase tracking-wider">Active Intelligence Located</h3>
                  </div>
                  <p className="text-4xl font-bold text-white mb-2">
                    {signalCount} Anomalies Found
                  </p>
                  <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
                    Our engine analyzed the available signals and prepared a safety profile. Join to view the full verification details.
                  </p>
                  <button
                    onClick={() => navigate('/plans')}
                    className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold py-3 px-10 rounded-full text-lg transition-all hover:scale-105 active:scale-95 shadow-lg shadow-emerald-500/20 w-full max-w-xs"
                  >
                    Reveal Findings
                  </button>
                  <p className="text-gray-500 text-sm mt-4">
                    Already have an account?{' '}
                    <Link to="/login" className="text-cyan-400 hover:text-cyan-300 transition font-semibold">
                      Login
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  )
}
